/**
 * @file Tiny hash router. Firebase Hosting serves a single index.html and
 * the app does the rest.
 *
 * Routes (post-rebuild):
 *   #/me                          employee  default landing for non-admins
 *   #/devices                     admin     device index
 *   #/devices/:id                 admin     device detail
 *   #/data-quality                admin     unassigned / orphan rollup
 *   #/reconcile                   admin     cross-system mismatch queue
 *   #/onboarding                  admin     joiners list (default landing)
 *   #/onboarding/:id              admin     per-employee shopping list
 *   #/offboarding                 admin     leavers list
 *   #/offboarding/:id             admin     per-employee gear + decisions
 *   #/settings                    admin     formula + sync panel + admins
 *
 * Default landing (no hash):
 *   admin → #/onboarding
 *   employee → #/me
 */

const listeners = new Set();

// Where the user navigated FROM (previous hash route this session).
// Powers context-aware "Back to …" links on detail pages: opening a person
// from Onboarding gets "Back to Onboarding", a device opened from a person
// page gets "Back to person", etc. Null on a fresh load / deep link —
// callers fall back to their default list route.
let lastRoute = null;
let prevRoute = null;
// True only while redirect() is dispatching, so the hashchange handler can
// tell a programmatic replace from a real user navigation.
let redirecting = false;

export function previousRoute() {
  return prevRoute;
}

export function getRoute() {
  const hash = location.hash || '#/';
  const segments = hash.slice(1).replace(/^\//, '').split('/').filter(Boolean);
  return {
    section: segments[0] || '',
    id:      segments[1] || null,
    extra:   segments.slice(2),
    raw:     hash,
  };
}

export function navigate(path) {
  const next = path.startsWith('#') ? path : `#${path}`;
  if (location.hash === next) return;
  location.hash = next;
}

/**
 * Replace-style navigation that does NOT add a history entry. Use for
 * role-gate redirects so the user can't back-arrow into a route they
 * just got bounced off of.
 *
 * A redirect is NOT a user navigation step, so it replaces `lastRoute`
 * instead of pushing it onto `prevRoute`. Without that, an alias route
 * (`#/onboarding/:id`, which main.js redirects to `#/people/:id`) became
 * the recorded "previous route" — so the detail page's Back link pointed
 * at a URL that instantly redirected the user right back to the page they
 * were trying to leave.
 */
export function redirect(path) {
  const next = path.startsWith('#') ? path : `#${path}`;
  if (location.hash === next) return;
  const url = `${location.pathname}${location.search}${next}`;
  redirecting = true;
  history.replaceState(null, '', url);
  // hashchange doesn't fire on replaceState, so dispatch manually.
  window.dispatchEvent(new HashChangeEvent('hashchange'));
  redirecting = false;
}

export function onRoute(cb) {
  listeners.add(cb);
  cb(getRoute());
  return () => listeners.delete(cb);
}

lastRoute = getRoute(); // initial route (deep link / reload) — prev stays null

window.addEventListener('hashchange', () => {
  const route = getRoute();
  // Ignore no-op transitions (redirect() re-dispatches on the same hash).
  if (!lastRoute || lastRoute.raw !== route.raw) {
    // Real navigation: the route we're leaving becomes `prevRoute`.
    // Redirect: REPLACE the current route, leaving `prevRoute` alone — the
    // page being redirected away from was never somewhere the user chose to
    // be, so "back" should return to whatever preceded it.
    if (!redirecting) prevRoute = lastRoute;
    lastRoute = route;
  }
  for (const cb of listeners) {
    try { cb(route); } catch (err) { console.error('[router] listener error', err); }
  }
});
